/**
 * Board view state lives in the URL, not in a store: it is shareable, survives a
 * refresh and works with the back button. Six of the old frontend's module-level
 * globals disappear here (docs/16 section 7.2).
 */
import type { Priority } from '../api/types'

export type Grouping = 'stage' | 'decision'
export type PriorityFilter = 'all' | Priority | 'review'

export interface BoardView {
  grouping: Grouping
  priority: PriorityFilter
  search: string
  /** The archive is a separate view of the board, not a filter within it. */
  archived: boolean
}

const DEFAULTS: BoardView = { grouping: 'stage', priority: 'all', search: '', archived: false }

function isGrouping(value: string | null): value is Grouping {
  return value === 'stage' || value === 'decision'
}

function isPriorityFilter(value: string | null): value is PriorityFilter {
  return value === 'all' || value === 'high' || value === 'medium' || value === 'low' || value === 'review'
}

export function parseBoardView(params: URLSearchParams): BoardView {
  const grouping = params.get('group')
  const priority = params.get('priority')
  return {
    grouping: isGrouping(grouping) ? grouping : DEFAULTS.grouping,
    priority: isPriorityFilter(priority) ? priority : DEFAULTS.priority,
    search: params.get('q') || DEFAULTS.search,
    archived: params.get('archived') === '1',
  }
}

/** Only non-default values are written, so a clean view has a clean URL. */
export function serialiseBoardView(view: BoardView): URLSearchParams {
  const params = new URLSearchParams()
  if (view.grouping !== DEFAULTS.grouping) params.set('group', view.grouping)
  if (view.priority !== DEFAULTS.priority) params.set('priority', view.priority)
  if (view.search.trim()) params.set('q', view.search.trim())
  if (view.archived) params.set('archived', '1')
  return params
}
